import React, { useState, useEffect, FunctionComponent } from 'react';
import styled from 'styled-components';
import { pick, omit } from 'lodash';

import { Button } from '@lt/components';
import { clipboardCopy, prepareScript } from './helpers';
import { useGetPartnerKey, useConstructorContextState } from '../../context/constructorContext';
import { useWidgetData } from '../../context/widgetDataContext';
import { WidgetName } from '../../models';

type Props = {
  globalParams?: unknown;
  widgetParams?: unknown;
  widgetName: WidgetName | undefined;
};

const CopyButton: FunctionComponent<Props> = ({ widgetName }) => {
  const [copy, setCopy] = useState(false);
  const state = useConstructorContextState();
  const partnerKey = useGetPartnerKey();
  const { data } = useWidgetData<Record<string, any>>();

  useEffect(() => {
    const timeoutID = window.setTimeout(() => {
      setCopy(false);
    }, 2000);
    return () => window.clearTimeout(timeoutID);
  }, [copy]);

  const handleCopy = () => {
    const searchParams = {
      ...pick(state, ['search_type', 'departure', 'adults', 'kids', 'start_date', 'nights']),
      ...(state.destination_ids && {
        destination_ids:
          widgetName !== WidgetName.SEARCH
            ? state.destination_ids.map((destination) => destination.value)
            : state.destination_ids,
      }),
      ...(data?.searchParams?.hotel_id && {
        hotel_id: data.searchParams.hotel_id,
        nights: data.searchParams.nights,
      }),
    };

    const widgetParams = pick(state, [
      'currency',
      'lang',
      'redirect',
      'isBlankBackground',
      'utmParams',
      'isPassLtAflt',
      'isWhitelabel',
      'whitelabelDomain',
      'whitelabelLogo',
      'whitelabelName',
      'isTabsSwitchOn',
    ]);

    const prepareParams = {
      widgetName,
      partnerKey,
      searchParams,
      widgetParams,
      ...(widgetName === WidgetName.PACKAGE && {
        widgetData: omit(data, 'package'),
      }),
    };

    const params = JSON.stringify(prepareParams);
    const widgetCode = prepareScript(params);

    clipboardCopy(widgetCode);
    setCopy(true);
  };

  if (!widgetName) {
    throw new Error(`[CopyButton] widgetName is not defined. widgetName = ${widgetName}`);
  }

  if (!partnerKey) {
    throw new Error(`[CopyButton] partnerKey is not defined, partnerKey = ${partnerKey}`);
  }

  return (
    <StyledCopyButton disabled={state.disabledCopy || copy} onClick={handleCopy} primary>
      {copy ? 'Скопированно' : 'Скопировать код виджета'}
    </StyledCopyButton>
  );
};

const StyledCopyButton = styled(Button)`
  // повышаем specificity чтобы при многократном использовании компонентов,
  // в разных виджетах на одной странице,
  // первичные стили не перетирали переопределенные
  && {
    position: fixed;
    width: 300px;
    bottom: 10px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
    font-size: 18px !important;
  }
`;

export default CopyButton;
